import { useState } from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useAppSocket } from '../providers/AppSocketContext';
import { useTranslation } from 'react-i18next';

interface Props {
  icon?: IconProp;
  text: string;
  tabKey: string;
  activeTab: string;
  setActiveTab: (key: string) => void;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  showText: boolean; // controls if text is shown (collapsed sidebar?)
  disabled?: boolean;
  selected?: boolean;
}

const NavItemLink = ({
  icon,
  text,
  tabKey,
  activeTab,
  setActiveTab,
  expandable = false,
  expanded = false,
  onToggle = () => {},
  showText,
  disabled,
  selected,
}: Props) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const { t } = useTranslation();

  const { appSettings } = useAppSocket();

  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || '#8b1010';
  const isActive = selected ?? activeTab === tabKey;

  const buttonContent = (
    <ListItemButton
      selected={isActive}
      disabled={disabled}
      onClick={() => setActiveTab(tabKey)}
      aria-current={isActive ? 'page' : undefined}
      sx={{
        minHeight: 46,
        justifyContent: showText ? 'initial' : 'center',
        borderRadius: 2,
        mx: showText ? 1 : 0.5,
        px: showText ? 1.25 : 0.75,
        py: 0.25,
        position: 'relative',

        // Base styles
        color: isActive ? '#ffffff' : 'text.primary',
        backgroundColor: isActive ? themeColor : 'transparent',
        border: `1px solid ${isActive ? alpha(themeColor, 0.5) : 'transparent'}`,

        // Hover & active states
        '&:hover': {
          backgroundColor: isActive
            ? themeColor // keep strong when active
            : 'action.hover',
          transform: 'translateY(-1px)',
          boxShadow: isActive
            ? `0 8px 18px ${alpha(themeColor, 0.34)}`
            : `0 6px 14px ${alpha(themeColor, 0.14)}`,
        },
        '&:active': {
          transform: 'translateY(0px) scale(0.99)',
        },
        '&:focus-visible': {
          outline: `2px solid ${alpha(themeColor, 0.65)}`,
          outlineOffset: 1,
        },

        // Selected / active styling
        '&.Mui-selected': {
          backgroundColor: themeColor,
          color: 'white',
          fontWeight: 600,
          boxShadow: `0 10px 20px ${alpha(themeColor, 0.35)}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            left: showText ? 8 : 4,
            top: 10,
            bottom: 10,
            width: showText ? 3 : 2,
            borderRadius: 4,
            bgcolor: alpha('#ffffff', 0.95),
          },
          '&:hover': {
            backgroundColor: themeColor, // or slightly darker if you want
          },
        },

        transition: 'background-color var(--motion-base) var(--motion-ease-standard), border-color var(--motion-base) var(--motion-ease-standard), transform var(--motion-fast) var(--motion-ease-standard), box-shadow var(--motion-base) var(--motion-ease-standard), color var(--motion-base) var(--motion-ease-standard)',
      }}
    >
      {icon && (
        <ListItemIcon
          sx={{
            minWidth: showText ? 38 : 'auto',
            color: isActive ? 'white' : 'inherit',
            transition: 'transform var(--motion-fast) var(--motion-ease-standard), color var(--motion-base) var(--motion-ease-standard)',
            transform: isActive ? 'scale(1.02)' : 'scale(1)',
          }}
        >
          <FontAwesomeIcon icon={icon} size="lg" />
        </ListItemIcon>
      )}

      {showText && (
        <ListItemText
          primary={text}
          primaryTypographyProps={{
            fontWeight: isActive ? 600 : 500,
            noWrap: true,
            fontSize: '0.93rem',
            letterSpacing: 0.1,
          }}
        />
      )}

      {expandable && showText && (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          size="small"
          aria-label={expanded ? t('common:ui.tooltips.collapseSection', { section: text }) : t('common:ui.tooltips.expandSection', { section: text })}
          aria-expanded={expanded}
          sx={{
            ml: 'auto',
            color: isActive ? 'white' : 'text.secondary',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform var(--motion-base) var(--motion-ease-emphasized), background-color var(--motion-fast) var(--motion-ease-standard), color var(--motion-base) var(--motion-ease-standard)',
            p: 0.5,
            borderRadius: 1,
            '&:hover': {
              bgcolor: isActive ? alpha('#ffffff', 0.16) : 'action.hover',
            },
          }}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </IconButton>
      )}
    </ListItemButton>
  );

  return (
    <ListItem disablePadding>
      {showText ? (
        // When sidebar is expanded → no tooltip needed
        buttonContent
      ) : (
        // Collapsed → wrap in Tooltip
        <Tooltip
          title={text}
          placement="right"
          open={tooltipOpen}
          onOpen={() => setTooltipOpen(true)}
          onClose={() => setTooltipOpen(false)}
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 3,
                fontSize: '0.78rem',
                fontWeight: 600,
              },
            },
            arrow: {
              sx: {
                color: 'background.paper',
              },
            },
          }}
        >
          {buttonContent}
        </Tooltip>
      )}
    </ListItem>
  );
};

export default NavItemLink;